import { Box, Checkbox as MuiCheckbox, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { type MouseEvent } from "react";

import { useIsMobile } from "../AppLayout/useResponsive";
import { Text } from "./Text";

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}

function UncheckedIcon({ isMobile }: { isMobile: boolean }) {
  return (
    <Box
      sx={(theme) => {
        const size = isMobile ? theme.size?.checkbox.mobile : theme.size?.checkbox.default;
        return {
          width: size,
          height: size,
          borderRadius: "50%",
          border: `1px solid ${theme.palette.border?.base}`,
          boxSizing: "border-box",
        };
      }}
    />
  );
}

function CheckedIcon({ isMobile }: { isMobile: boolean }) {
  return (
    <Box
      sx={(theme) => {
        const size = isMobile ? theme.size?.checkbox.mobile : theme.size?.checkbox.default;
        return {
          width: size,
          height: size,
          borderRadius: "50%",
          backgroundColor: theme.palette.primary.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        };
      }}
    >
      <Box
        component="svg"
        sx={(theme) => {
          const iconSize = isMobile
            ? theme.size?.checkbox.iconMobile
            : theme.size?.checkbox.iconDefault;
          return { width: iconSize, height: iconSize };
        }}
        viewBox="0 0 12 12"
        fill="none"
      >
        <path
          d="M10 3L4.5 8.5L2 6"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
    </Box>
  );
}

/**
 * Berlin-style checkbox with round shape, thin light-grey border,
 * and label styled to match the redesign typography.
 */
export function Checkbox(props: CheckboxProps) {
  const { checked, onChange, label, ariaLabel, disabled = false, sx, onClick } = props;
  const isMobile = useIsMobile();

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          alignItems: "center",
          gap: 2,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
        },
        sx
      )}
      onClick={(event) => {
        onClick?.(event);

        if (!disabled) {
          onChange(!checked);
        }
      }}
    >
      <MuiCheckbox
        checked={checked}
        disabled={disabled}
        icon={<UncheckedIcon isMobile={isMobile} />}
        checkedIcon={<CheckedIcon isMobile={isMobile} />}
        inputProps={{ "aria-label": ariaLabel ?? label }}
        sx={{
          padding: 0,
          "&:hover": { backgroundColor: "transparent" },
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
      {label && (
        <Text semibold variant="caption">
          {label}
        </Text>
      )}
    </Box>
  );
}
